/**
 * Grille horaire du planning (vues Jour et Semaine) : heures sur le côté, une
 * colonne par jour, rendez-vous posés à leur heure et à leur durée.
 *
 * Refonte du 08/10/2026 : les blocs étaient décalés de 70 px dans leur colonne,
 * se touchaient, restaient vides dès qu'ils passaient sous l'en-tête collant, et
 * une absence de plusieurs jours remplissait toute la hauteur de la grille.
 *
 * 15/10/2026 : les tâches planifiées (« Tâche » en tête) et les notes libres y
 * ont leur bloc. Un objectif est un repère à son heure limite (« Objectif
 * 12:00 · titre »), coloré selon son état : à faire, bloqué, tenu ou non atteint.
 *
 * Décision du propriétaire du 15 octobre 2026 :
 * - une réunion posée pour plusieurs personnes (créneaux d'une même série, même
 *   heure, même titre) est UN bloc « Titre · N personnes », pas un bloc par
 *   personne (`groupSeriesEvents`) ; il ouvre la réunion et ses participants ;
 * - sur ordinateur, qui a le droit la déplace en la glissant à une autre heure
 *   (`onMoveEvent`) ; au doigt et au clavier, on l'ouvre et on change l'heure ;
 * - une heure approximative s'écrit « ≈ 14:00 », bloc en pointillés.
 *
 * 17/10/2026 (interface v4) : un seul « Ajouter » par jour, dans son en-tête,
 * remplace les 48 boutons invisibles par colonne (336 sur une semaine) ; au
 * doigt ou à la souris, toucher un moment libre de la grille propose encore d'y
 * poser quelque chose à cette heure-là.
 *
 * 24/10/2026 : la grille montre les heures habituelles de l'entreprise, élargies
 * à ce qui est posé (`visibleHours`) ; en vue Jour, ni date ni « Ajouter » en
 * tête, ils sont dans la barre du planning.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type DragEvent, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { approxClock, approxMarginLabel, clockMinutes, DEFAULT_WORKING_HOURS, type WorkingHours } from '@myequip/shared';
import { t } from '../../core/i18n.js';
import type { PlanningEvent, PlanningIntervention, PlanningObjective, PlanningTask } from './planning-data.js';
import type { EventLinkState } from './event-form.js';
import { BillingBadge } from '../interventions/BillingBadge.js';
import { OBJECTIVE_STATE_LABELS, objectiveState } from '../tasks/objective.js';
import { AddDayButton } from './AddDayButton.js';
import { Badge } from '../../components/ui/Badge.js';

/**
 * Écart vertical entre deux objectifs à la même minute : la hauteur de leur
 * cible tactile (44 px), pour que ni les repères ni les zones à toucher ne se
 * recouvrent.
 */
const DEADLINE_STACK_PX = 44;

/** Hauteur minimale d'un bloc, en % de la journée : une demi-heure. */
const MIN_HEIGHT = (30 / 1440) * 100;
/**
 * Un temps bloqué de 8 h ou plus sur une journée (formation, congé, journée
 * d'école…) passe dans la rangée « Journée » : en bloc, il écrasait les
 * interventions de la colonne en les réduisant à une fraction de largeur.
 */
const ALL_DAY_HEIGHT = (8 * 60 * 100) / 1440;
/** Sous 50 minutes affichées, le bloc ne garde qu'une ligne. */
const COMPACT_HEIGHT = (50 / 1440) * 100;
/** Écart minimal, en minutes, entre le repère « maintenant » et une étiquette d'heure affichée. */
const NOW_LABEL_CLEARANCE_MINUTES = 20;

export function daySegments(
  start: string,
  end: string | null,
  day: Date,
): { top: number; height: number } | null {
  const midnight = new Date(day);
  midnight.setHours(0, 0, 0, 0);
  const next = new Date(midnight);
  next.setDate(next.getDate() + 1);
  const begins = new Date(start);
  const ends = end ? new Date(end) : new Date(begins.getTime() + 60 * 60_000);
  if (
    !Number.isFinite(begins.getTime()) ||
    !Number.isFinite(ends.getTime()) ||
    begins >= next ||
    ends <= midnight ||
    ends <= begins
  )
    return null;
  const clippedStart = begins < midnight ? midnight : begins;
  const clippedEnd = ends > next ? next : ends;
  const top = clippedStart.getHours() * 60 + clippedStart.getMinutes();
  const bottom =
    clippedEnd.getTime() === next.getTime()
      ? 1440
      : clippedEnd.getHours() * 60 + clippedEnd.getMinutes();
  const topPercent = (top / 1440) * 100;
  const height = Math.max(MIN_HEIGHT, ((bottom - top) / 1440) * 100);
  // Un rendez-vous de 23 h 50 ne doit pas dépasser sous la grille.
  return { top: Math.min(topPercent, 100 - height), height };
}

/**
 * Répartit les rendez-vous simultanés en colonnes. Le nombre de colonnes est
 * celui du GROUPE qui se chevauche, pas celui de la journée : deux rendez-vous
 * simultanés le matin ne rétrécissent plus celui, seul, de l'après-midi.
 */
export function calendarLanes<T extends { starts_at: string; ends_at: string | null }>(
  items: T[],
  day: Date,
): { item: T; segment: { top: number; height: number }; lane: number; lanes: number }[] {
  const placed = items
    .map((item) => ({ item, segment: daySegments(item.starts_at, item.ends_at, day) }))
    .filter((v): v is { item: T; segment: { top: number; height: number } } => v.segment !== null)
    .sort((a, b) => a.segment.top - b.segment.top || b.segment.height - a.segment.height);
  const result: {
    item: T;
    segment: { top: number; height: number };
    lane: number;
    lanes: number;
  }[] = [];
  let group: typeof result = [];
  let groupEnd = -1;
  let laneEnds: number[] = [];
  const closeGroup = (): void => {
    for (const entry of group) entry.lanes = laneEnds.length;
    result.push(...group);
    group = [];
    laneEnds = [];
  };
  for (const v of placed) {
    // Tolérance d'arrondi : 11:00 → 11:00 se suivent, ils ne se chevauchent pas.
    if (group.length > 0 && v.segment.top >= groupEnd - 1e-6) closeGroup();
    let lane = laneEnds.findIndex((end) => end <= v.segment.top + 1e-6);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = v.segment.top + v.segment.height;
    groupEnd = Math.max(group.length > 0 ? groupEnd : -1, laneEnds[lane]!);
    group.push({ ...v, lane, lanes: 1 });
  }
  closeGroup();
  return result;
}

/** Un créneau du planning et les personnes qui y sont (une seule hors réunion d'équipe). */
export type PlanningEventGroup = PlanningEvent & { attendees: { id: string; name: string }[] };

/**
 * Une réunion pour toute l'équipe donnait un petit bloc par personne. Les créneaux
 * d'une même série, à la même heure, de même nature et de même titre deviennent
 * UN créneau avec la liste de ses participants. Le créneau représentant est celui
 * de la personne qui regarde quand elle y est (elle peut toujours l'ouvrir).
 * Un créneau sans série reste seul.
 */
export function groupSeriesEvents(events: PlanningEvent[], meId?: string): PlanningEventGroup[] {
  const groups = new Map<string, PlanningEventGroup>();
  const result: PlanningEventGroup[] = [];
  for (const event of events) {
    const key = event.series_id
      ? [event.series_id, event.starts_at, event.ends_at, event.kind, event.title,
        event.approval_status ?? '', event.time_approx_minutes ?? ''].join('|')
      : undefined;
    const existing = key ? groups.get(key) : undefined;
    if (existing) {
      if (!existing.attendees.some((person) => person.id === event.user_id)) {
        existing.attendees.push({ id: event.user_id, name: event.user_name });
      }
      if (event.user_id === meId) {
        existing.id = event.id;
        existing.user_id = event.user_id;
        existing.user_name = event.user_name;
      }
      continue;
    }
    const group: PlanningEventGroup = { ...event, attendees: [{ id: event.user_id, name: event.user_name }] };
    if (key) groups.set(key, group);
    result.push(group);
  }
  for (const group of result) group.attendees.sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  return result;
}

/** « Réunion d'équipe · 6 personnes », ou le titre seul pour une personne. */
export function groupTitle(event: PlanningEventGroup): string {
  return event.attendees.length > 1 ? `${event.title} · ${event.attendees.length} personnes` : event.title;
}

/** « Anne, Bruno, Chloé » : qui est à ce créneau. */
export function groupPeople(event: PlanningEventGroup): string {
  return event.attendees.map((person) => person.name).join(', ');
}

type GridItem =
  | (PlanningIntervention & { source: 'intervention'; title: string; subtitle: string })
  | (PlanningEventGroup & { source: 'event'; subtitle: string; group: PlanningEventGroup })
  | (PlanningTask & { source: 'task'; subtitle: string });

const timeOf = (iso: string): string =>
  new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

/** Pas du glisser-déposer : un quart d'heure. */
const DROP_STEP_MINUTES = 15;
/** Pas d'un moment touché dans la grille : une demi-heure. */
const TAP_STEP_MINUTES = 30;

/** « 14:00 – 17:00 », ou « lun. 14:00 → mer. 10:00 » sur plusieurs jours. */
export function eventRange(startsAt: string, endsAt: string | null, approxMinutes?: number | null): string {
  // Heure approximative : l'heure de fin n'a plus de sens, on donne la marge.
  if (approxMinutes) return `${approxClock(startsAt, approxMinutes)} (${approxMarginLabel(approxMinutes)})`;
  if (!endsAt) return timeOf(startsAt);
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  if (sameDay(start, end)) return `${timeOf(startsAt)} – ${timeOf(endsAt)}`;
  const day = (date: Date): string => date.toLocaleDateString('fr-FR', { weekday: 'short' });
  return `${day(start)} ${timeOf(startsAt)} → ${day(end)} ${timeOf(endsAt)}`;
}

/** Page de modification d'un temps bloqué. */
export const eventPath = (id: string): string => `/planning/temps-bloque/${id}`;

/**
 * La tâche dans la page Tâches, sous l'onglet où elle se trouve : celles
 * reçues sous « Mes tâches », celles données sous « Données à l'équipe ».
 */
export function taskPath(task: Pick<PlanningTask, 'id' | 'status' | 'assignee_id'>, meId: string | undefined): string {
  const view = task.status === 'faite' ? 'done' : task.assignee_id === meId ? null : 'given';
  return `/taches?${view ? `vue=${view}&` : ''}tache=${encodeURIComponent(task.id)}`;
}

/**
 * Heures montrées par la grille (24/10/2026) : la journée habituelle de
 * l'entreprise (Mon entreprise › Heures habituelles) avec une heure de marge
 * de chaque côté, élargie à tout ce qui est posé ces jours-là pour ne jamais
 * rien cacher. Les segments sont en % de la journée, comme `daySegments`.
 */
export function visibleHours(
  hours: WorkingHours | undefined,
  segments: readonly { top: number; height: number }[],
): { start: number; end: number } {
  const base = hours ?? DEFAULT_WORKING_HOURS;
  let start = Math.floor(clockMinutes(base.start) / 60) - 1;
  let end = Math.ceil(clockMinutes(base.end) / 60) + 1;
  for (const segment of segments) {
    start = Math.min(start, Math.floor((segment.top * 14.4) / 60));
    end = Math.max(end, Math.ceil(((segment.top + segment.height) * 14.4) / 60));
  }
  start = Math.max(0, start);
  end = Math.min(24, Math.max(end, start + 1));
  return { start, end };
}

/** L'heure d'un moment touché dans une colonne, à la demi-heure, dans les heures montrées. */
export function tappedTime(offsetY: number, columnHeight: number, startMinutes = 0, spanMinutes = 1440): string {
  const raw = startMinutes + (columnHeight > 0 ? (offsetY / columnHeight) * spanMinutes : 0);
  const last = startMinutes + spanMinutes - TAP_STEP_MINUTES;
  const minutes = Math.min(last, Math.max(startMinutes, Math.floor(raw / TAP_STEP_MINUTES) * TAP_STEP_MINUTES));
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const longDay = (day: Date): string =>
  day.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

export function TimeGrid(props: {
  from: Date;
  to: Date;
  interventions: PlanningIntervention[];
  events: PlanningEvent[];
  tasks: PlanningTask[];
  objectives: PlanningObjective[];
  meId: string | undefined;
  returnTo: string;
  hereId: string | undefined;
  /** « Ajouter » d'une journée, ou un moment libre touché dans sa colonne. */
  onAdd?: ((day: Date, time?: string) => void) | undefined;
  onDay: (date: Date) => void;
  /** Ce créneau peut-il être glissé par la personne qui regarde (droit du planning) ? */
  canMoveEvent?: (event: PlanningEventGroup) => boolean;
  /** Créneau déposé à une autre heure : la page confirme puis l'enregistre. */
  onMoveEvent?: (event: PlanningEventGroup, startsAt: Date) => void;
  /** Heures habituelles de l'entreprise : la grille s'y cale. */
  workingHours?: WorkingHours | undefined;
}): ReactNode {
  const scroll = useRef<HTMLDivElement>(null);
  // Glisser n'existe qu'à la souris : au doigt, on ouvre le créneau et on change l'heure.
  const [finePointer] = useState(
    () => typeof window !== 'undefined' && Boolean(window.matchMedia?.('(pointer: fine)').matches),
  );
  const dragging = useRef<{ event: PlanningEventGroup; grabMinutes: number; durationMinutes: number } | undefined>(undefined);
  const [dropPreview, setDropPreview] = useState<{ day: number; minutes: number; duration: number }>();
  const head = useRef<HTMLDivElement>(null);
  const hereRef = useRef<HTMLAnchorElement>(null);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const days = useMemo(() => {
    const list: Date[] = [];
    for (const d = new Date(props.from); d < props.to; d.setDate(d.getDate() + 1))
      list.push(new Date(d));
    return list;
  }, [props.from, props.to]);
  const week = days.length > 1;

  const { timed, allDay } = useMemo(() => {
    const items: GridItem[] = [
      ...props.interventions.map((item) => ({
        ...item,
        source: 'intervention' as const,
        title: item.customer_name,
        subtitle: item.assignee_name ?? 'Non affecté',
      })),
      ...groupSeriesEvents(props.events, props.meId).map((item) => ({
        ...item,
        // `source` et non `kind` : un événement porte déjà son propre `kind`
        // (congé, formation…), qu'il ne faut pas écraser.
        source: 'event' as const,
        title: groupTitle(item),
        subtitle: groupPeople(item),
        // Le créneau tel qu'il est (titre réel) : c'est lui qu'on déplace.
        group: item,
      })),
      ...props.tasks.map((item) => ({
        ...item,
        source: 'task' as const,
        subtitle: item.assignee_name,
      })),
    ];
    const allDayByDay = days.map((day) =>
      items.filter((item): item is Extract<GridItem, { source: 'event' }> => {
        if (item.source !== 'event') return false;
        const segment = daySegments(item.starts_at, item.ends_at, day);
        return segment !== null && segment.height >= ALL_DAY_HEIGHT;
      }),
    );
    const timedByDay = days.map((day, index) =>
      calendarLanes(
        items.filter((item) => !allDayByDay[index]!.some((e) => e.id === item.id)),
        day,
      ),
    );
    return { timed: timedByDay, allDay: allDayByDay };
  }, [days, props.interventions, props.events, props.tasks, props.meId]);

  // Fenêtre d'heures montrée, et conversion d'une position de la journée (en %) vers la colonne (fraction).
  const hours = useMemo(() => visibleHours(props.workingHours, [
    ...timed.flatMap((list) => list.map((entry) => entry.segment)),
    ...days.flatMap((day) => props.objectives
      .filter((objective) => sameDay(new Date(objective.due_at), day))
      .map((objective) => {
        const due = new Date(objective.due_at);
        return { top: ((due.getHours() * 60 + due.getMinutes()) / 1440) * 100, height: 0 };
      })),
  ]), [props.workingHours, timed, days, props.objectives]);
  const startMinutes = hours.start * 60;
  const spanMinutes = (hours.end - hours.start) * 60;
  const atMinutes = (minutes: number): number => (minutes - startMinutes) / spanMinutes;
  const atPercent = (percent: number): number => atMinutes(percent * 14.4);

  /** Minute du jour visée par le pointeur, au quart d'heure, le haut du bloc gardé sous le doigt. */
  const dropMinutes = (event: DragEvent<HTMLElement>): number => {
    const rect = event.currentTarget.getBoundingClientRect();
    const raw = startMinutes + ((event.clientY - rect.top) / rect.height) * spanMinutes - (dragging.current?.grabMinutes ?? 0);
    const snapped = Math.round(raw / DROP_STEP_MINUTES) * DROP_STEP_MINUTES;
    return Math.min(1440 - DROP_STEP_MINUTES, Math.max(0, snapped));
  };
  const dropHandlers = (index: number) => props.onMoveEvent ? {
    onDragOver: (event: DragEvent<HTMLDivElement>) => {
      if (!dragging.current) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      const minutes = dropMinutes(event);
      if (dropPreview?.day !== index || dropPreview.minutes !== minutes) {
        setDropPreview({ day: index, minutes, duration: dragging.current.durationMinutes });
      }
    },
    onDragLeave: (event: DragEvent<HTMLDivElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropPreview(undefined);
    },
    onDrop: (event: DragEvent<HTMLDivElement>) => {
      const drag = dragging.current;
      if (!drag) return;
      event.preventDefault();
      const at = new Date(days[index]!);
      at.setHours(0, dropMinutes(event), 0, 0);
      dragging.current = undefined;
      setDropPreview(undefined);
      if (at.getTime() !== new Date(drag.event.starts_at).getTime()) props.onMoveEvent?.(drag.event, at);
    },
  } : {};

  /**
   * Un moment libre touché (doigt ou souris) propose d'y poser quelque chose à
   * cette heure. Rien n'est focalisable ici : au clavier et au lecteur d'écran,
   * c'est le bouton « Ajouter » de la journée.
   */
  const tapHandler = (day: Date) => props.onAdd ? {
    onClick: (event: MouseEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) return;
      const rect = event.currentTarget.getBoundingClientRect();
      props.onAdd?.(day, tappedTime(event.clientY - rect.top, rect.height, startMinutes, spanMinutes));
    },
  } : {};

  // Repères d'objectifs : position de l'heure limite dans la journée, et rang
  // parmi ceux de la même minute.
  const deadlines = useMemo(
    () =>
      days.map((day) => {
        const seen = new Map<number, number>();
        return props.objectives
          .filter((objective) => sameDay(new Date(objective.due_at), day))
          .map((objective) => {
            const due = new Date(objective.due_at);
            const minutes = due.getHours() * 60 + due.getMinutes();
            const stack = seen.get(minutes) ?? 0;
            seen.set(minutes, stack + 1);
            return { objective, top: (minutes / 1440) * 100, stack };
          });
      }),
    [days, props.objectives],
  );

  const todayIndex = days.findIndex((day) => sameDay(day, now));
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const nowAt = atMinutes(nowMinutes);
  // Le repère « maintenant » n'est posé que dans les heures montrées.
  const showNow = todayIndex >= 0 && nowAt >= 0 && nowAt <= 1;
  const containsHere = (index: number): boolean =>
    Boolean(
      timed[index]?.some(({ item }) => item.source === 'intervention' && item.id === props.hereId),
    );
  const hereDayIndex = containsHere(todayIndex)
    ? todayIndex
    : timed.findIndex((_, index) => containsHere(index));
  const hasAllDay = allDay.some((list) => list.length > 0);
  // Le plus grand nombre de rendez-vous côte à côte : au doigt, les colonnes
  // s'élargissent pour que chacun reste une cible franche.
  const maxLanes = Math.max(1, ...timed.flatMap((list) => list.map((entry) => entry.lanes)));
  const firstDayKey = days[0]?.getTime();

  // Position de départ : l'intervention en cours, sinon le premier rendez-vous
  // du jour, sinon le début de la journée de travail. Jamais minuit.
  useLayoutEffect(() => {
    const own = scroll.current;
    if (!own) return;
    // Sur téléphone, c'est la page du planning qui défile (un seul défilement) ;
    // ailleurs, la grille garde le sien. On positionne celui qui défile vraiment.
    const sheet = own.closest<HTMLElement>('.planning-page--sheet');
    const container = sheet && getComputedStyle(sheet).overflowY === 'auto' ? sheet : own;
    const body = own.querySelector<HTMLElement>('.planning-timegrid__day');
    if (!body) return;
    // Ce qui reste collé en haut de la zone : l'en-tête des jours et, quand la
    // page défile, la barre de période au-dessus de lui.
    const pinned = container === own ? 0
      : (sheet?.querySelector<HTMLElement>('.planning-toolbar')?.offsetHeight ?? 0);
    const headHeight = (head.current?.offsetHeight ?? 0) + pinned;
    const bodyTop = container === own
      ? body.offsetTop
      : body.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    const focusIndex = todayIndex >= 0 ? todayIndex : 0;
    const firstTop = hereRef.current
      ? hereRef.current.offsetTop
      : // Le premier rendez-vous du jour, où qu'il soit ; 7 h si la journée est vide.
        Math.min(
          ...(timed[focusIndex] ?? []).map(({ segment }) => atPercent(segment.top) * body.offsetHeight),
          // Journée vide : le haut des heures montrées, déjà calé sur la journée de travail.
          (timed[focusIndex] ?? []).length > 0 ? Infinity : 0,
        );
    // L'en-tête collant masque le haut de la zone : on vise juste dessous.
    container.scrollTop = Math.max(0, bodyTop + firstTop - headHeight - 24);
    // Semaine sur téléphone : la colonne du jour (ou de l'intervention en
    // cours) arrive à l'écran au lieu du lundi.
    const column = own.querySelectorAll<HTMLElement>('.planning-timegrid__day')[
      hereDayIndex >= 0 ? hereDayIndex : focusIndex
    ];
    const gutter = own.querySelector<HTMLElement>('.planning-timegrid__gutter');
    if (column && container.scrollWidth > container.clientWidth)
      container.scrollLeft = column.offsetLeft - (gutter?.offsetWidth ?? 0);
    // L'intervention en cours est amenée à l'écran, page comprise : sur
    // téléphone la grille commence parfois sous la ligne de flottaison.
    hereRef.current?.scrollIntoView({ block: 'center', inline: 'nearest' });
    // Uniquement au changement de période ou d'intervention en cours : on ne
    // reprend pas la main sur le défilement à chaque rafraîchissement.
  }, [firstDayKey, days.length, props.hereId]);

  return (
    <section className="planning-timegrid" aria-label="Agenda horaire">
      <div
        ref={scroll}
        className="planning-timegrid__scroll"
        tabIndex={0}
        role="group"
        aria-label="Agenda horaire défilant"
      >
        <div
          className={`planning-timegrid__inner${week ? ' planning-timegrid__inner--week' : ''}`}
          style={{ '--tg-days': days.length, '--tg-lanes': maxLanes, '--tg-hours': hours.end - hours.start }}
        >
          {/* Vue Jour (24/10/2026) : la date et « Ajouter » sont déjà dans la barre du
              planning ; l'en-tête ne garde que la rangée « Journée » s'il y en a une. */}
          {week || hasAllDay ? (
          <div ref={head} className="planning-timegrid__head">
            {week ? <div className="planning-timegrid__corner" aria-hidden="true" /> : null}
            {!week ? null : days.map((day, index) => (
              <div
                key={day.toISOString()}
                className={`planning-timegrid__dayhead${index === todayIndex ? ' is-today' : ''}`}
              >
                <button
                  type="button"
                  className="planning-timegrid__date"
                  onClick={() => props.onDay(day)}
                  aria-current={index === todayIndex ? 'date' : undefined}
                >
                  {/* Nom lu = texte visible puis l'action (WCAG 2.5.3). */}
                  <span className="planning-timegrid__weekday">
                    {day.toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', '')}
                  </span>{' '}
                  <span className="planning-timegrid__daynum">{day.getDate()}</span>
                  <span className="sr-only"> — ouvrir la journée du {longDay(day)}</span>
                </button>
                {props.onAdd ? <AddDayButton day={day} compact onAdd={(date) => props.onAdd?.(date)} /> : null}
              </div>
            ))}
            {hasAllDay ? (
              <>
                <div className="planning-timegrid__corner planning-timegrid__corner--allday">Journée</div>
                {days.map((day, index) => (
                  <div key={day.toISOString()} className="planning-timegrid__allday">
                    {allDay[index]!.map((event) => (
                      <Link
                        key={event.id}
                        to={eventPath(event.id)}
                        state={{ from: props.returnTo }}
                        className={`planning-timegrid__chip${
                          event.approval_status === 'en_attente' ? ' planning-timegrid__chip--pending' : ''
                        }`}
                        title={`${event.title} · ${eventRange(event.starts_at, event.ends_at)} · ${event.subtitle}`}
                      >
                        <strong>{event.title}</strong> {event.attendees.length > 1 ? '' : event.user_name}
                      </Link>
                    ))}
                  </div>
                ))}
              </>
            ) : null}
          </div>
          ) : null}

          <div className="planning-timegrid__gutter" aria-hidden="true">
            {Array.from({ length: hours.end - hours.start - 1 }, (_, index) => {
              const hour = hours.start + index + 1;
              // L'étiquette d'heure que le repère « maintenant » recouvrirait s'efface.
              return showNow && Math.abs(hour * 60 - nowMinutes) < NOW_LABEL_CLEARANCE_MINUTES ? null : (
                <span key={hour} style={{ '--tg-at': atMinutes(hour * 60) }}>
                  {String(hour).padStart(2, '0')}:00
                </span>
              );
            })}
            {/* L'heure courante est écrite dans la marge : un trait rouge seul,
                posé en travers d'un rendez-vous, ne disait pas ce qu'il était. */}
            {showNow ? (
              <span className="planning-timegrid__nowlabel" style={{ '--tg-at': nowAt }}>
                {now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            ) : null}
          </div>

          {days.map((day, index) => (
            <div
              key={day.toISOString()}
              className={`planning-timegrid__day${index === todayIndex ? ' is-today' : ''}`}
              {...dropHandlers(index)}
              {...tapHandler(day)}
            >
              {dropPreview?.day === index ? (
                <div
                  className="planning-timegrid__drop"
                  aria-hidden="true"
                  style={{
                    '--tg-at': atMinutes(dropPreview.minutes),
                    '--tg-span': Math.max(30, dropPreview.duration) / spanMinutes,
                  }}
                >
                  {`${String(Math.floor(dropPreview.minutes / 60)).padStart(2, '0')}:${String(dropPreview.minutes % 60).padStart(2, '0')}`}
                </div>
              ) : null}
              {index === todayIndex && showNow ? (
                <div
                  className="planning-timegrid__now"
                  style={{ '--tg-at': nowAt }}
                  role="img"
                  aria-label={`Heure actuelle : ${now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`}
                />
              ) : null}
              <div className="planning-timegrid__events">
                {timed[index]?.map(({ item, segment, lane, lanes }) => {
                  const isHere =
                    item.source === 'intervention' &&
                    item.id === props.hereId &&
                    index === hereDayIndex;
                  // Place du bloc dans la journée, en fractions (variables CSS seulement).
                  const place = {
                    top: atPercent(segment.top), height: (segment.height * 14.4) / spanMinutes, left: lane / lanes, width: 1 / lanes,
                  };
                  const compact = segment.height < COMPACT_HEIGHT;
                  const approx = item.time_approx_minutes ?? null;
                  const start = approxClock(item.starts_at, approx);
                  const range = eventRange(item.starts_at, item.ends_at, approx);
                  const approxClass = approx ? ' planning-timegrid__event--approx' : '';
                  // « Tâche » en tête : sur la grille, rien d'autre ne la distingue d'un rendez-vous.
                  const label = item.source === 'task' ? (
                    <strong><span className="planning-timegrid__kind">Tâche</span> {item.title}</strong>
                  ) : (
                    <strong>{item.title}</strong>
                  );
                  const content = compact ? (
                    <span className="planning-timegrid__line">
                      {label} {start}
                    </span>
                  ) : (
                    <>
                      {label}
                      <span>{range}</span>
                      <span>{item.subtitle}</span>
                      {item.source === 'intervention' ? (
                        <BillingBadge status={item.billing_status} compact />
                      ) : null}
                    </>
                  );
                  if (item.source === 'task') {
                    const done = item.status === 'faite';
                    return (
                      <Link
                        key={`task-${item.id}`}
                        to={taskPath(item, props.meId)}
                        state={{ from: props.returnTo }}
                        className={`planning-timegrid__event planning-timegrid__event--task${done ? ' planning-timegrid__event--task-done' : ''}${
                          compact ? ' planning-timegrid__event--compact' : ''
                        }${approxClass}`}
                        style={{ '--tg-top': place.top, '--tg-height': place.height, '--tg-left': place.left, '--tg-width': place.width }}
                        title={`Tâche${done ? ' faite' : ''} · ${item.title} · ${range} · ${item.subtitle}`}
                      >
                        {content}
                      </Link>
                    );
                  }
                  if (item.source === 'event') {
                    const group = item.attendees.length > 1;
                    // Un créneau d'un seul jour se glisse ; un congé sur plusieurs jours s'ouvre.
                    const movable = finePointer && Boolean(props.onMoveEvent)
                      && sameDay(new Date(item.starts_at), new Date(item.ends_at))
                      && (props.canMoveEvent?.(item.group) ?? false);
                    const state: EventLinkState = { from: props.returnTo, group };
                    return (
                      <Link
                        key={item.id}
                        to={eventPath(item.id)}
                        state={state}
                        className={`planning-timegrid__event ${item.kind === 'note' ? 'planning-timegrid__event--note' : 'planning-timegrid__event--absence'}${
                          item.approval_status === 'en_attente' ? ' planning-timegrid__event--pending' : ''
                        }${compact ? ' planning-timegrid__event--compact' : ''}${approxClass}${group ? ' planning-timegrid__event--group' : ''}`}
                        style={{ '--tg-top': place.top, '--tg-height': place.height, '--tg-left': place.left, '--tg-width': place.width }}
                        title={`${item.title} · ${range} · ${item.subtitle}${movable ? ' · glisser pour déplacer' : ''}`}
                        {...(movable ? {
                          draggable: true,
                          onDragStart: (event: DragEvent<HTMLAnchorElement>) => {
                            const column = event.currentTarget.closest<HTMLElement>('.planning-timegrid__day');
                            const rect = column?.getBoundingClientRect();
                            const block = event.currentTarget.getBoundingClientRect();
                            dragging.current = {
                              event: item.group,
                              grabMinutes: rect ? ((event.clientY - block.top) / rect.height) * spanMinutes : 0,
                              durationMinutes: (Date.parse(item.ends_at) - Date.parse(item.starts_at)) / 60_000,
                            };
                            event.dataTransfer.effectAllowed = 'move';
                            event.dataTransfer.setData('text/plain', item.title);
                          },
                          onDragEnd: () => {
                            dragging.current = undefined;
                            setDropPreview(undefined);
                          },
                        } : {})}
                      >
                        {content}
                      </Link>
                    );
                  }
                  const classes = [
                    'planning-timegrid__event',
                    `planning-timegrid__event--${item.status}`,
                    item.priority === 'urgente' && 'planning-timegrid__event--urgent',
                    isHere && 'planning-timegrid__event--here',
                    compact && 'planning-timegrid__event--compact',
                    approx && 'planning-timegrid__event--approx',
                  ].filter(Boolean).join(' ');
                  // Créneau occupé : l'heure et la personne, sans lien (la fiche serait refusée).
                  if (item.restricted) {
                    return (
                      <div key={item.id} className={classes} style={{ '--tg-top': place.top, '--tg-height': place.height, '--tg-left': place.left, '--tg-width': place.width }} title={`${item.title} · ${range} · ${item.subtitle}`}>
                        {content}
                      </div>
                    );
                  }
                  return (
                    <Link
                      key={item.id}
                      to={`/interventions/${item.id}`}
                      state={{ from: props.returnTo }}
                      ref={isHere ? hereRef : undefined}
                      aria-current={isHere ? 'location' : undefined}
                      title={`${item.title} · ${range} · ${item.subtitle}`}
                      className={classes}
                      style={{ '--tg-top': place.top, '--tg-height': place.height, '--tg-left': place.left, '--tg-width': place.width }}
                    >
                      {isHere ? <Badge tone="info">{t('planning.vousEtesIci')}</Badge> : null}
                      {content}
                    </Link>
                  );
                })}
              </div>
              {deadlines[index]?.map(({ objective, top, stack }) => {
                const state = objectiveState(objective);
                const time = timeOf(objective.due_at);
                return (
                  <div
                    key={`objective-${objective.id}`}
                    className={`planning-timegrid__deadline planning-timegrid__deadline--${state}`}
                    style={{ '--tg-at': atPercent(top) }}
                  >
                    <Link
                      to={taskPath(objective, props.meId)}
                      state={{ from: props.returnTo }}
                      className="planning-timegrid__deadline-pill"
                      style={{ '--tg-stack': `${stack * DEADLINE_STACK_PX}px` }}
                      title={`Objectif ${time} · ${objective.title} · ${objective.assignee_name} · ${OBJECTIVE_STATE_LABELS[state]}`}
                    >
                      <span className="planning-timegrid__deadline-text">
                        <span className="planning-timegrid__kind">Objectif</span> {time} · {objective.title}
                        <span className="sr-only">
                          {` · ${objective.assignee_name} · ${OBJECTIVE_STATE_LABELS[state]}`}
                        </span>
                      </span>
                    </Link>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
